import React, { createContext, useCallback, useContext, useMemo } from 'react';

import enLocale from './en.json';
import zhLocale from './zh.json';

// ==================== Types ====================

export type LocaleLang = ('en' | 'zh') | string;

type LocaleMap = Record<string, string | string[]>;

const localeResources: Record<LocaleLang, LocaleMap> = {
  en: enLocale as unknown as LocaleMap,
  zh: zhLocale as unknown as LocaleMap,
};

// ==================== Context ====================

interface LocaleContextValue {
  lang: LocaleLang;
  /** 获取翻译文本，支持 {key} 插值，t函数随lang变化更新 */
  t: (key: string, params?: Record<string, string | number>) => string;
  /** 获取数组类型的翻译（如月份列表），t函数随lang变化更新 */
  tList: (key: string) => string[];
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

// ==================== Provider ====================

export interface LocaleProviderProps {
  /** 语言，默认 'en' */
  lang?: LocaleLang;
  children: React.ReactNode;
}

/**
 * 组件库语言 Provider
 *
 * @example
 * ```tsx
 * <LocaleProvider lang="zh">
 *   <App />
 * </LocaleProvider>
 * ```
 */
export const LocaleProvider = ({ lang = 'en', children }: LocaleProviderProps) => {
  const currentLocale = useMemo(() => localeResources[lang] || localeResources.en, [lang]);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      const raw = currentLocale[key];
      if (raw === undefined || typeof raw !== 'string') {
        // fallback: 尝试 en
        const fallback = localeResources.en[key];
        if (typeof fallback === 'string') {
          return replaceParams(fallback, params);
        }
        return key; // 找不到就返回 key 本身
      }
      return replaceParams(raw, params);
    },
    [currentLocale],
  );

  const tList = useCallback(
    (key: string): string[] => {
      const raw = currentLocale[key];
      if (Array.isArray(raw)) return raw;
      // fallback
      const fallback = localeResources.en[key];
      if (Array.isArray(fallback)) return fallback;
      return [];
    },
    [currentLocale],
  );

  const value = useMemo<LocaleContextValue>(
    () => ({ lang, t, tList }),
    [lang, t, tList],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

// ==================== Hook ====================

/**
 * 获取组件库的国际化 t / tList 方法。
 *
 * 如果没有外层 `<LocaleProvider>`，会自动使用英文 fallback，不会报错。
 *
 * @example
 * ```tsx
 * const { t, tList, lang } = useComponentLocale();
 * t('Dialog.cancel');                      // "Cancel"
 * t('Table.totalItems', { total: 100 });   // "Total 100 items"
 * tList('DatePicker.months');              // ["January", ...]
 * ```
 */
export const useComponentLocale = () => {
  const ctx = useContext(LocaleContext);
  // 未包裹 Provider 时，提供默认英文 fallback
  if (!ctx) {
    return defaultLocaleValue;
  }
  return ctx;
};

// ==================== Helpers ====================

function replaceParams(text: string, params?: Record<string, string | number>): string {
  if (!params) return text;
  return text.replace(/\{\{(\w+)\}\}/g, (_, key) => {
    return params[key] !== undefined ? String(params[key]) : `{{${key}}}`;
  });
}

/** 没有 Provider 时的默认英文 fallback */
const defaultT = (key: string, params?: Record<string, string | number>): string => {
  const raw = localeResources.en[key];
  if (typeof raw === 'string') return replaceParams(raw, params);
  return key;
};

const defaultTList = (key: string): string[] => {
  const raw = localeResources.en[key];
  return Array.isArray(raw) ? raw : [];
};

const defaultLocaleValue: LocaleContextValue = {
  lang: 'en',
  t: defaultT,
  tList: defaultTList,
};

export default LocaleProvider;
